// src/features/players/schemas/PlayerForm.schema.ts

import { z } from 'zod';
import { PLAYER_CONSTRAINTS } from '@/lib/constants/player';

// Note: Enum fields use z.string() (not z.enum()) to match the form data interfaces
// which declare these as `string`. The constants provide valid values for Select options
// in the UI, but Zod validation focuses on structural correctness, not enum membership.

// --- Overview tab ---

export const overviewFormSchema = z.object({
  label: z.string()
    .min(PLAYER_CONSTRAINTS.LABEL_MIN, 'Name is required')
    .max(PLAYER_CONSTRAINTS.LABEL_MAX, `Max ${PLAYER_CONSTRAINTS.LABEL_MAX} characters`),
  descShort: z.string()
    .max(PLAYER_CONSTRAINTS.DESC_SHORT_MAX, `Max ${PLAYER_CONSTRAINTS.DESC_SHORT_MAX} characters`),
  enabled: z.boolean(),
  allowSharing: z.boolean(),
  facebook: z.boolean(),
  twitter: z.boolean(),
  email: z.boolean(),
});

// --- Settings tab ---

export const settingsFormSchema = z.object({
  // Playback
  autoplay: z.boolean(),
  loop: z.boolean(),
  volume: z.number(),
  playsinline: z.boolean(),
  resumePlayback: z.boolean(),
  allowSeekForward: z.boolean(),
  allowDownload: z.boolean(),
  fullscreen: z.boolean(),
  showCaptions: z.boolean(),
  showDvr: z.boolean(),
  showTitle: z.boolean(),
  videoAudioMode: z.string(),
  // Sizing
  responsive: z.boolean(),
  width: z.number().int().positive('Width must be a positive number'),
  height: z.number().int().positive('Height must be a positive number'),
  // Settings2
  allowCasting: z.boolean(),
  allowPIP: z.boolean(),
  allowSkipSecs: z.number().nullable(),
  allowPlaybackRate: z.boolean(),
  showViewerCount: z.boolean(),
});

// --- Advertising tab ---

export const adFormSchema = z.object({
  adSourceType: z.string(),
  adSourceVasturl1: z.string(),
  adSourceListId: z.number().nullable(),
  adPreroll: z.boolean(),
  adMidrollInjectType: z.string(),
  adMidrollAutoBreakInterval: z.number(),
  adBreakMaxDuration: z.number()
    .min(PLAYER_CONSTRAINTS.AD_BREAK_MAX_DURATION_MIN, `Min ${PLAYER_CONSTRAINTS.AD_BREAK_MAX_DURATION_MIN}`)
    .max(PLAYER_CONSTRAINTS.AD_BREAK_MAX_DURATION_MAX, `Max ${PLAYER_CONSTRAINTS.AD_BREAK_MAX_DURATION_MAX}`),
  adNobreakInterval: z.number(),
  adBumperListId: z.number().nullable(),
}).superRefine((data, ctx) => {
  if (data.adSourceType === 'vast') {
    if (!data.adSourceVasturl1 || data.adSourceVasturl1.length < PLAYER_CONSTRAINTS.VAST_URL_MIN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `VAST URL is required (min ${PLAYER_CONSTRAINTS.VAST_URL_MIN} characters)`,
        path: ['adSourceVasturl1'],
      });
    }
  }
  if (data.adSourceType === 'playlist') {
    if (data.adSourceListId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Ad playlist is required',
        path: ['adSourceListId'],
      });
    }
  }
  if (data.adMidrollInjectType === 'auto') {
    if (!data.adMidrollAutoBreakInterval || data.adMidrollAutoBreakInterval <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Auto break interval is required',
        path: ['adMidrollAutoBreakInterval'],
      });
    }
  }
});

// --- Security tab ---

export const securityFormSchema = z.object({
  accessControlType: z.string(),
  password: z.string(),
}).superRefine((data, ctx) => {
  if (data.accessControlType === 'password') {
    if (!data.password || data.password.length < PLAYER_CONSTRAINTS.PASSWORD_MIN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Password is required',
        path: ['password'],
      });
    }
    if (data.password.length > PLAYER_CONSTRAINTS.PASSWORD_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Max ${PLAYER_CONSTRAINTS.PASSWORD_MAX} characters`,
        path: ['password'],
      });
    }
  }
});

// --- Appearance tab ---

export const appearanceFormSchema = z.object({
  overlayPosition: z.string(),
  overlayClickUrl: z.string()
    .refine(
      (val) => !val || (val.length >= PLAYER_CONSTRAINTS.OVERLAY_CLICK_URL_MIN && val.length <= PLAYER_CONSTRAINTS.OVERLAY_CLICK_URL_MAX),
      `Must be empty or ${PLAYER_CONSTRAINTS.OVERLAY_CLICK_URL_MIN}-${PLAYER_CONSTRAINTS.OVERLAY_CLICK_URL_MAX} characters`
    ),
  plBarLabel: z.string()
    .max(PLAYER_CONSTRAINTS.PLAYLIST_BAR_LABEL_MAX, `Max ${PLAYER_CONSTRAINTS.PLAYLIST_BAR_LABEL_MAX} characters`),
  plBarBgcolor: z.string(),
  plBarFgcolor: z.string(),
  plShowDate: z.boolean(),
  plShowDescShort: z.boolean(),
});
